"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { setComments } from "../../../store/ApplicationSlice";
import { ApplicationDetailsCard } from "../../../components/application/application-details-card";
import { CompanyDetailsCard } from "../../../components/application/company-details-card";
import { AgentDetailsCard } from "../../../components/application/agent-details-card";
import { DocumentsSection } from "../../../components/application/documents-section";
import { DecisionMatrixCard } from "../../../components/application/decision-matrix-card";
import { DecisionGrid } from "../../../components/application/decision-grid";
import { NotesTab } from "../../../components/application/notes-tab";
import { HistoryTab } from "../../../components/application/history-tab";
import { AIGeneratedIndicator } from "../../../components/ui/ai-generated-indicator";
import { Card } from "../../../components/ui/card";
import { useDispatch, useSelector } from "react-redux";
import fetchData from "../../../utils/fetchData";
import { CommentPin } from "./Comments";
import { selectedApplicationData } from "../../../store/ApplicationSlice";
import { getNotesByApplicationId } from "../../../api/notesService";

export function getRandomColor() {
  // Generate a random number between 0 and 0xFFFFFF, then convert to hex and pad with zeros
  const randomColor = Math.floor(Math.random() * 0xffffff)
    .toString(16)
    .padStart(6, "0");
  return `#${randomColor}`;
}

export default function ApplicationDetailsPage() {
  const dispatch = useDispatch();
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const comments = useSelector((state) => state.intakeData.comments);
  const attachments = useSelector((state) => state.intakeData.attachments);
  const selectedIndex = useSelector((state) => state.intakeData.selectedIndex);
  const [statusList, setStatusList] = useState([]);
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
  const username = userDetails.name || "user";
  const appetiteData = useSelector((state) => state.intakeData.appetiteData);
  const selectedApplicationId = useSelector(
    (state) => state.intakeData.selectedAppId
  );
  const selApplicationData = useSelector(
    (state) => state.intakeData.selectedApplicationData
  );

  useEffect(() => {
    if (selectedApplicationId && appetiteData.length > 0) {
      dispatch(
        selectedApplicationData(
          appetiteData.find(
            (app) => app.application_id === selectedApplicationId
          )
        )
      );
    }
  }, [selectedApplicationId, appetiteData, dispatch]);

  useEffect(() => {
    if (!selectedApplicationId) return;

    async function fetchNotes() {
      try {
        const response = await getNotesByApplicationId(
          selectedApplicationId,
          accessToken,
          refreshToken
        );
        dispatch(setComments(response));
      } catch (error) {
        console.error("Error fetching notes:", error);
      }
    }

    fetchNotes();
  }, [selectedApplicationId, accessToken, refreshToken, dispatch]);

  useEffect(() => {
    fetchData(
      `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/application_status?application_id=${selApplicationData?.application_id}`,
      "GET"
    ).then((data) => {
      setStatusList(data);
    });
  }, [selApplicationData]);

  const handleCanvasClick = (e) => {
    if (e.target.closest(".comment-box")) return;

    const canvas = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - canvas.left;
    const y = e.clientY - canvas.top;
    console.log(canvas, e.currentTarget.closest("[data-section]"));
    const sectionEl = e.target.closest("[data-section]");
    const sectionId = sectionEl?.getAttribute("data-section") || "unknown";
    console.log({ sectionEl, sectionId });
    const newComment = {
      id: crypto.randomUUID(),
      application_id: selectedApplicationId,
      x,
      y,
      page_number: 0,
      section_id: sectionId,
      tab: activeTab,
      text: "",
      user: {
        name: username,
        initials: username ? username.charAt(0).toUpperCase() : "",
        color: getRandomColor(),
      },
      is_resolved: false,
      is_editing: true,
      is_open: true,
    };

    dispatch(setComments([...comments, newComment]));
  };

  const filteredComments = useMemo(
    () =>
      Array.isArray(comments)
        ? comments.filter((c) => c.tab === activeTab)
        : [],
    [comments, activeTab]
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Application Content */}
        <div className="flex-1 overflow-auto">
          {selApplicationData && (
            <div className="p-6 max-w-[1600px] mx-auto">
              <ApplicationDetailsCard
                isFullWidth={isFullWidth}
                setIsFullWidth={setIsFullWidth}
                selectedApplicationData={selApplicationData || {}}
              />

              {/* Main Content Tabs */}
              <Tabs
                defaultValue="overview"
                className="mb-6"
                onValueChange={setActiveTab}
                value={activeTab}
              >
                <TabsList className="bg-white rounded-lg shadow-sm p-1 w-full justify-start">
                  <TabsTrigger
                    value="overview"
                    className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger
                    value="notes"
                    className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                  >
                    Notes
                  </TabsTrigger>
                  {/* <TabsTrigger
                  value="history"
                  className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                >
                  History
                </TabsTrigger>
                <TabsTrigger
                  value="decision"
                  className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                >
                  Decision Grid
                </TabsTrigger> */}
                </TabsList>

                {/* Overview Tab Content */}
                <TabsContent value="overview" className="mt-4 space-y-4">
                  <div
                    className="relative"
                    onDoubleClick={handleCanvasClick}
                    style={{ minHeight: 600 }}
                  >
                    {/* Comments */}
                    <div className="absolute top-0 left-0 z-9999 pointer-events-none">
                      {filteredComments?.map((comment, index) => {
                        const isDocumentComment =
                          comment.section_id?.startsWith("document-details-");

                        if (isDocumentComment) {
                          const selectedAttachment =
                            attachments?.[selectedIndex];
                          const expectedSectionId = `document-details-${selectedAttachment?.doc_id}-${selectedAttachment?.name}`;
                          if (comment.section_id === expectedSectionId) {
                            return (
                              <div
                                key={`${comment.application_id} + ${index}`}
                                style={{
                                  zIndex: 100,
                                  pointerEvents: "auto",
                                  position: "absolute",
                                  left: comment.x,
                                  top: comment.y,
                                }}
                              >
                                <CommentPin {...comment} />
                              </div>
                            );
                          }
                          return null; // Not matching selected document — skip
                        }

                        // Non-document comments (e.g., company-details)
                        return (
                          <div
                            key={comment.id}
                            style={{
                              zIndex: 100,
                              pointerEvents: "auto",
                              position: "absolute",
                              left: comment.x,
                              top: comment.y,
                            }}
                          >
                            <CommentPin {...comment} />
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                      <CompanyDetailsCard
                        data-section="company-details"
                        statusList={statusList}
                        selectedApplicationData={selApplicationData || {}}
                      />
                      <AgentDetailsCard
                        data-section="agent-details"
                        selectedApplicationData={selApplicationData || {}}
                      />
                    </div>
                    <DocumentsSection
                      data-section="document-details"
                      username={username}
                    />
                    <DecisionMatrixCard
                      data-section="decision-matrix-details"
                      selectedApplicationData={selApplicationData || {}}
                    />
                  </div>
                </TabsContent>

                {/* Notes Tab Content */}
                <TabsContent value="notes" className="mt-4">
                  <NotesTab />
                </TabsContent>

                {/* History Tab Content */}
                <TabsContent value="history" className="mt-4">
                  <HistoryTab />
                </TabsContent>

                {/* Decision Grid Tab Content */}
                <TabsContent value="decision" className="mt-4">
                  <Card className="p-5 bg-white shadow-sm">
                    <h2 className="text-lg font-semibold mb-4">
                      Application Decision Grid
                    </h2>
                    <DecisionGrid
                      selectedApplicationData={selApplicationData}
                    />
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
          {/* AI Generated Indicator */}
          <AIGeneratedIndicator />
        </div>
      </div>
    </div>
  );
}
